import React from 'react'
// libs
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd'
import { useSelector } from 'react-redux'
import { formValueSelector } from 'redux-form'
import { useMutation } from '@apollo/client/react'
// components
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import { Input } from 'components/FormElements'
import ControlButtons from '../ControlButtons'
// icons
import LinkIcon from '@mui/icons-material/Link'
import RefreshIcon from '@mui/icons-material/Refresh'
// graphql
import { INJURY_MASTER_CLAIM_NUMBER_REFRESH_MUTATION } from 'graphql/injuryMaster/mutations'
// types
import { IField, IInjuryMasterLink } from 'types/graphql'
// constants
import { Forms } from 'constants/forms'
import { Modals } from 'constants/modals'
// context
import { PublicContext } from 'containers/Risks/Risk/Edit/public'
import { RiskAPIContext } from 'containers/Risks/Risk/Edit/layers/API'
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// hooks
import { useShowModal } from 'hooks/useShowModal'
// themes
import { colors } from 'themes/index'
// views
import {
  ClaimNumberRow,
  IncidentIdInput,
  Collapse,
  Content,
  LinkItem,
  LinksList,
  LinksPlaceholder,
  SectionName,
  Wrapper
} from './views'

interface Props {
  field: IField
  dragHandleProps?: DraggableProvidedDragHandleProps
}

const selector = formValueSelector(Forms.RISK_TEMPLATE_FORM)

const InjuryMasterLinks: React.FC<Props> = ({ field, dragHandleProps }) => {
  const { isRiskTemplate, deleteField } = React.useContext(RiskTemplateAPIContext)
  const { risk } = React.useContext(RiskAPIContext)

  const isPublic = React.useContext(PublicContext)

  const { showModal } = useShowModal({ modalType: Modals.CONFIRM_DELETE_RISK_MODAL })

  const [refreshClaimNumber, { loading: isRefreshing }] = useMutation(INJURY_MASTER_CLAIM_NUMBER_REFRESH_MUTATION)

  const [isCollapsed, setExpanded] = React.useState(false)

  // { incidentId, links }: derived by the backend from the record's Injury Master incident id,
  // with links to the incident's pages in Injury Master. Not stored with the record's field values.
  const value: { incidentId?: string; claimNumber?: string; links?: IInjuryMasterLink[] } | undefined = useSelector(
    (state) => selector(state, `${field.id}.value`)
  )
  const incidentId = value?.incidentId
  const claimNumber = value?.claimNumber
  const links = value?.links ?? []

  const canRefreshClaimNumber = !isRiskTemplate && !isPublic && !!risk?.id && !!incidentId

  const onCollapseClick = () => setExpanded(!isCollapsed)

  const onRefreshClaimNumberClick = () => {
    refreshClaimNumber({ variables: { riskId: risk.id, fieldId: field.id } })
  }

  const onDeleteClick = () => {
    showModal({
      width: 'sm',
      entity: 'field',
      handleConfirm: () => deleteField(field)
    })
  }

  const bgColor = field.backgroundColor || colors.gray190

  return (
    <Wrapper $color={bgColor} data-testid='injury-master-links-field' data-entity-id={field.id}>
      {isRiskTemplate && <ControlButtons field={field} onDeleteClick={onDeleteClick} />}
      <Collapse $color={bgColor} isCollapsed={isCollapsed} handleClick={onCollapseClick}>
        <SectionName $color={bgColor} {...dragHandleProps}>
          {field.label}
        </SectionName>
      </Collapse>
      {!isCollapsed && (
        <Content>
          <IncidentIdInput>
            <Input
              disabled
              fullWidth
              value={incidentId ?? ''}
              label='Injury Master Incident Id'
              name='injuryMasterIncidentId'
            />
          </IncidentIdInput>
          <ClaimNumberRow>
            <IncidentIdInput>
              <Input disabled fullWidth value={claimNumber ?? ''} label='Claim Number' name='injuryMasterClaimNumber' />
            </IncidentIdInput>
            {canRefreshClaimNumber && (
              <Tooltip title='Refresh claim number from Injury Master'>
                <IconButton
                  size='small'
                  disabled={isRefreshing}
                  onClick={onRefreshClaimNumberClick}
                  data-testid='injury-master-claim-number-refresh'>
                  <RefreshIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
          </ClaimNumberRow>
          <LinksList>
            {links.map(({ label, url }) => (
              <LinkItem
                key={label}
                href={url}
                target='_blank'
                rel='noopener noreferrer'
                data-testid='injury-master-links-link'>
                <LinkIcon fontSize='small' />
                {label}
              </LinkItem>
            ))}
            {!links.length && <LinksPlaceholder>Links appear once the case has been created</LinksPlaceholder>}
          </LinksList>
        </Content>
      )}
    </Wrapper>
  )
}

export default InjuryMasterLinks
